/**
 * Xero Accounts Component
 * Displays and manages Xero accounts
 */

import React from 'react';
import './XeroAccounts.css';

const XeroAccounts = ({ accounts }) => {
  // Ensure accounts is always an array to prevent map errors
  const safeAccounts = Array.isArray(accounts) ? accounts : [];
  

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    // Handle Xero date format: /Date(1730756239390+0000)/
    if (dateString.startsWith('/Date(') && dateString.endsWith(')/')) {
      const timestamp = parseInt(dateString.match(/\d+/)[0]);
      return new Date(timestamp).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    
    // Handle regular date strings
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  if (safeAccounts.length === 0) {
    return (
      <div className="xero-accounts">
        <div className="no-accounts">
          <h3>Xero Accounts</h3>
          <div className="empty-state">
            <div className="empty-icon">🏢</div>
            <h4>No Xero Accounts Found</h4>
            <p>No Xero accounts are currently connected or available.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="xero-accounts">
      <div className="accounts-header">
        <h3>Xero Accounts</h3>
        <p>All Xero chart of accounts with detailed information</p>
      </div>

      <div className="accounts-table-container">
        <table className="accounts-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th>Type</th>
              <th>Status</th>
              <th>Tax Type</th>
              <th>Class</th>
              <th>Description</th>
              <th>Reporting Code</th>
              <th>Bank Account Type</th>
              <th>System Account</th>
              <th>Enable Payments</th>
              <th>Show in Expense Claims</th>
              <th>Has Attachments</th>
              <th>Add to Watchlist</th>
              <th>Updated Date</th>
            </tr>
          </thead>
          <tbody>
            {safeAccounts.map(account => (
              <tr key={account.AccountID}>
                <td className="account-code">{account.Code}</td>
                <td className="account-name">{account.Name}</td>
                <td className="account-type">
                  <span className={`type-badge ${account.Type?.toLowerCase()}`}>
                    {account.Type}
                  </span>
                </td>
                <td className="account-status">
                  <span className={`status-badge ${account.Status?.toLowerCase()}`}>
                    {account.Status}
                  </span>
                </td>
                <td className="tax-type">{account.TaxType}</td>
                <td className="account-class">{account.Class}</td>
                <td className="description">{account.Description || 'N/A'}</td>
                <td className="reporting-code">
                  {account.ReportingCode ? `${account.ReportingCode} - ${account.ReportingCodeName}` : 'N/A'}
                </td>
                <td className="bank-account-type">{account.BankAccountType || 'N/A'}</td>
                <td className="system-account">{account.SystemAccount || 'N/A'}</td>
                <td className="enable-payments">
                  <span className={`boolean-badge ${account.EnablePaymentsToAccount ? 'true' : 'false'}`}>
                    {account.EnablePaymentsToAccount ? 'Yes' : 'No'}
                  </span>
                </td>
                <td className="expense-claims">
                  <span className={`boolean-badge ${account.ShowInExpenseClaims ? 'true' : 'false'}`}>
                    {account.ShowInExpenseClaims ? 'Yes' : 'No'}
                  </span>
                </td>
                <td className="attachments">
                  <span className={`boolean-badge ${account.HasAttachments ? 'true' : 'false'}`}>
                    {account.HasAttachments ? 'Yes' : 'No'}
                  </span>
                </td>
                <td className="watchlist">
                  <span className={`boolean-badge ${account.AddToWatchlist ? 'true' : 'false'}`}>
                    {account.AddToWatchlist ? 'Yes' : 'No'}
                  </span>
                </td>
                <td className="updated-date">{formatDate(account.UpdatedDateUTC)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="accounts-summary">
        <h4>Summary</h4>
        <div className="summary-stats">
          <div className="summary-stat">
            <span className="stat-label">Total Accounts:</span>
            <span className="stat-value">{safeAccounts.length}</span>
          </div>
          <div className="summary-stat">
            <span className="stat-label">Active:</span>
            <span className="stat-value">
              {safeAccounts.filter(acc => acc.Status === 'ACTIVE').length}
            </span>
          </div>
          <div className="summary-stat">
            <span className="stat-label">Revenue Accounts:</span>
            <span className="stat-value">
              {safeAccounts.filter(acc => acc.Type === 'REVENUE').length}
            </span>
          </div>
          <div className="summary-stat">
            <span className="stat-label">Expense Accounts:</span>
            <span className="stat-value">
              {safeAccounts.filter(acc => acc.Type === 'EXPENSE').length}
            </span>
          </div>
          <div className="summary-stat">
            <span className="stat-label">Asset Accounts:</span>
            <span className="stat-value">
              {safeAccounts.filter(acc => acc.Type === 'FIXED' || acc.Type === 'CURRENT' || acc.Type === 'INVENTORY').length}
            </span>
          </div>
          <div className="summary-stat">
            <span className="stat-label">Liability Accounts:</span>
            <span className="stat-value">
              {safeAccounts.filter(acc => acc.Type === 'CURRLIAB' || acc.Type === 'TERMLIAB').length}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default XeroAccounts;
